/**
 * The NOVA weekly check-in: what she answers, what a completed run records,
 * and the weekly-move job's contract. A run's `result` is parsed with
 * {@link weeklyCheckInResultSchema}; nothing else stores her answers. The
 * move she picks becomes a "For me" task, and that task is the only place
 * its status lives. Pure and universal.
 *
 * Aliases: weekly check-in, weekly review, weekly move, check-in answers
 */

import { z } from "zod";

/** Where the check-in page lives, under My assets › Grow. */
export const WEEKLY_CHECK_IN_PATH =
  "/my-coaching/business/nova/assets/check-in";

export const LAST_MOVE_OUTCOMES = ["done", "partly", "not_yet"] as const;

export type LastMoveOutcome = (typeof LAST_MOVE_OUTCOMES)[number];

export const LAST_MOVE_OUTCOME_LABELS = {
  done: "Done",
  partly: "Partly",
  not_yet: "Not yet",
} as const satisfies Record<LastMoveOutcome, string>;

export const HELD_BACK_REASONS = [
  "time",
  "not_sure_how",
  "not_ready",
  "something_else",
] as const;

export type HeldBackReason = (typeof HELD_BACK_REASONS)[number];

export const HELD_BACK_REASON_LABELS = {
  time: "Time",
  not_sure_how: "Not sure how",
  not_ready: "Didn't feel ready",
  something_else: "Something else",
} as const satisfies Record<HeldBackReason, string>;

/** Typed numbers are small whole counts; empty means she left it blank. */
const typedCountSchema = z.number().int().min(0).max(9999).nullable();

/** DCM's counts for the 7 days before she opened the check-in. */
export const weeklyDcmCountsSchema = z.object({
  leads: z.number().int().min(0),
  enquiries: z.number().int().min(0),
  sessionsHeld: z.number().int().min(0),
  novaApproved: z.number().int().min(0),
});

export type WeeklyDcmCounts = z.infer<typeof weeklyDcmCountsSchema>;

export const weeklyTypedNumbersSchema = z.object({
  conversations: typedCountSchema,
  newClients: typedCountSchema,
});

export type WeeklyTypedNumbers = z.infer<typeof weeklyTypedNumbersSchema>;

export const lastMoveAnswerSchema = z
  .object({
    outcome: z.enum(LAST_MOVE_OUTCOMES),
    reason: z.enum(HELD_BACK_REASONS).nullable(),
    note: z.string().trim().max(280).nullable(),
  })
  .refine(
    (answer) => answer.outcome !== "done" || answer.reason === null,
    "A move that is done has no reason",
  );

export type LastMoveAnswer = z.infer<typeof lastMoveAnswerSchema>;

/** One move: what to do, why it matters now, and about how long it takes. */
export const weeklyMoveSchema = z.object({
  title: z.string().trim().min(1).max(140),
  why: z.string().trim().min(1).max(400),
  minutes: z.number().int().min(5).max(480),
  /** A page in NOVA where the move happens, when it has one. */
  href: z.string().startsWith("/my-coaching/").nullable(),
});

export type WeeklyMove = z.infer<typeof weeklyMoveSchema>;

/** Where the move she saved came from. */
export const WEEKLY_MOVE_SOURCES = ["nova", "plan", "home", "own"] as const;

export type WeeklyMoveSource = (typeof WEEKLY_MOVE_SOURCES)[number];

/** What a completed check-in run records in `result`. */
export const weeklyCheckInResultSchema = z.object({
  counts: weeklyDcmCountsSchema,
  typed: weeklyTypedNumbersSchema,
  lastMove: z
    .object({
      taskId: z.string().uuid().nullable(),
      title: z.string(),
      answer: lastMoveAnswerSchema,
    })
    .nullable(),
  move: weeklyMoveSchema.extend({
    taskId: z.string().uuid(),
    source: z.enum(WEEKLY_MOVE_SOURCES),
  }),
  win: z.string().trim().max(280).nullable(),
});

export type WeeklyCheckInResult = z.infer<typeof weeklyCheckInResultSchema>;

/** What she sends when she finishes the check-in. */
export const weeklyCheckInAnswersSchema = z.object({
  runId: z.string().uuid(),
  typed: weeklyTypedNumbersSchema,
  lastMove: lastMoveAnswerSchema.nullable(),
  move: weeklyMoveSchema.extend({ source: z.enum(WEEKLY_MOVE_SOURCES) }),
  win: z.string().trim().max(280).nullable(),
});

export type WeeklyCheckInAnswers = z.infer<typeof weeklyCheckInAnswersSchema>;

// ── The weekly-move job (intent `weekly_move`) ─────────────────────────────

/** A build-path step NOVA may point the move at. */
const homeStepSchema = z.object({
  assetKey: z.string().min(1),
  label: z.string().min(1),
  status: z.string().min(1),
});

export const weeklyMoveInputSchema = z.object({
  plan: z
    .object({
      milestone: z.string().min(1),
      targetDate: z.string(),
      actions: z.array(z.string().min(1)).max(12),
      weeklyRhythm: z.array(z.string().min(1)).max(8),
    })
    .nullable(),
  homeSteps: z.array(homeStepSchema).max(5),
  lastMove: z
    .object({
      title: z.string().min(1),
      outcome: z.enum(LAST_MOVE_OUTCOMES),
      reason: z.enum(HELD_BACK_REASONS).nullable(),
      note: z.string().nullable(),
    })
    .nullable(),
  counts: weeklyDcmCountsSchema,
  typed: weeklyTypedNumbersSchema,
});

export type WeeklyMoveInput = z.infer<typeof weeklyMoveInputSchema>;

/** A move as the model writes it: it names a build-path step, not a URL. */
const proposedMoveSchema = z.object({
  title: z.string().trim().min(1).max(140),
  why: z.string().trim().min(1).max(400),
  minutes: z.number().int().min(5).max(480),
  assetKey: z.string().nullable().optional(),
});

export type ProposedWeeklyMove = z.infer<typeof proposedMoveSchema>;

export const weeklyMoveOutputSchema = z.object({
  move: proposedMoveSchema,
  alternatives: z.array(proposedMoveSchema).max(2),
  weekNote: z.string().trim().min(1).max(400),
  suggestedWin: z.string().trim().min(1).max(200).nullable(),
});

export type WeeklyMoveOutput = z.infer<typeof weeklyMoveOutputSchema>;

// ── Streak and welcome back ────────────────────────────────────────────────

/** A run as the streak reads it, newest first. */
export interface CheckInRunStatus {
  readonly status: "open" | "completed" | "missed";
}

/**
 * Check-ins completed in a row, counting back from the newest run. An open
 * run is skipped, so the count is what she has kept up to now.
 */
export function checkInStreak(
  runsNewestFirst: readonly CheckInRunStatus[],
): number {
  let streak = 0;
  for (const run of runsNewestFirst) {
    if (run.status === "open") continue;
    if (run.status !== "completed") break;
    streak += 1;
  }
  return streak;
}

/**
 * She missed the check-in before this one: the run just before the newest
 * was missed. The page then says "Welcome back", never that a streak was
 * lost.
 */
export function isWelcomeBack(
  runsNewestFirst: readonly CheckInRunStatus[],
): boolean {
  return runsNewestFirst[1]?.status === "missed";
}
